import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import LoginModal from '../components/Common/LoginModal';
import RegisterAdminModal from '../components/Common/RegisterAdminModal';
import Header from '../pages/Header';
import bannerImage from '../assets/images/banner.png';
import EventsSection from './EventsSection';
import Footer from '../components/Footer';
import './Home.css';

// Importación de iconos para darle el aspecto visual intuitivo de la imagen 1.jpeg
import { 
    FaStopwatch, FaClipboardList, FaBroadcastTower, FaUserClock, FaRunning, 
    FaBoxOpen, FaCubes, FaLightbulb, FaCreditCard, FaTrophy, 
    FaChevronRight, FaCalendarAlt, FaWhatsapp, FaStar, FaShieldAlt, FaCode
} from 'react-icons/fa';
import { FiZap, FiHeadphones, FiShield } from "react-icons/fi";
import { LuShieldCheck } from "react-icons/lu";
import { LuHeadset } from "react-icons/lu";
import {
    LuActivity,
    LuTimer,
    LuMonitor,
    LuRadio,
    LuUsers,
    LuShoppingBag,
    LuTicket,
    LuStar
  } from "react-icons/lu";

const Home = () => {
    const [userRole, setUserRole] = useState(null);
    const [username, setUsername] = useState('');
    const [isModalOpen, setModalOpen] = useState(false);
    const [registerModalOpen, setRegisterModalOpen] = useState(false);

    // Datos fijos mapeados directamente en cuadrícula (sin ocultar pestañas)
    const products = [
        { icon: <LuTimer style={{ stroke: "url(#blue-purple-gradient)" }}/>, title: 'Cronometraje con chip', desc: 'Resultados precisos para running, trail, ciclismo, MTB y triatlón.' },
        { icon: <LuMonitor />, title: 'Plataforma de inscripciones', desc: 'Registros online, pagos, categorías y control de participantes.' },
        { icon: <LuRadio style={{ stroke: "url(#blue-purple-gradient)" }}/>, title: 'Resultados en vivo', desc: 'Seguimiento en tiempo real desde cualquier dispositivo.' },
        { icon: <LuUsers style={{ stroke: "url(#blue-purple-gradient)" }}/>, title: 'Asesoramiento de eventos', desc: 'Acompañamiento técnico y organizativo en cada etapa de tu evento.' },
        { icon: <LuShoppingBag style={{ stroke: "url(#blue-purple-gradient)" }}/>, title: 'Entrega de kits', desc: 'Control y distribución eficiente para tus participantes.' },
        { icon: <LuTicket style={{ stroke: "url(#blue-purple-gradient)" }}/>, title: 'Material para eventos', desc: 'Arcos, vallas, carpas, dorsales y todo el equipamiento.' }
    ];

    const pasosProceso = [
        { icon: <FaLightbulb />, title: 'Planificación', desc: 'Te asesoramos desde la idea inicial.' },
        { icon: <FaClipboardList />, title: 'Inscripciones', desc: 'Plataforma online fácil y segura.' },
        { icon: <FaCreditCard />, title: 'Pagos', desc: 'Múltiples medios de pago integrados.' },
        { icon: <FaStopwatch />, title: 'Cronometraje', desc: 'Tecnología precisa y confiable.' },
        { icon: <LuRadio />, title: 'Resultados', desc: 'En vivo y al instante para todos.' },
        { icon: <FaTrophy />, title: 'Premiación', desc: 'Cerramos tu evento con éxito.' }
    ];

    const testimonios = [
        { img: '/images/t1.jpg', name: 'Martín Rodríguez', event: 'Desafío Patagonia', text: '"El proceso de inscripción fue impecable. La plataforma es muy fácil de usar y el soporte excelente."' },
        { img: '/images/t2.jpg', name: 'Laura Fernández', event: 'Maratón de la Mujer', text: '"Los resultados estuvieron disponibles inmediatamente. Muy profesionales."' },
        { img: '/images/t3.jpg', name: 'Diego Salazar', event: 'Trail de las Sierras', text: '"Nos ayudaron a aumentar nuestros inscritos y mejorar toda la experiencia del evento."' }
    ];

    useEffect(() => {
        const updateUserState = () => {
            const role = localStorage.getItem('role');
            const username = localStorage.getItem('username');
            setUserRole(role);
            setUsername(username);
        };
        updateUserState();
        window.addEventListener('storage', updateUserState);
        return () => window.removeEventListener('storage', updateUserState);
    }, []);

    const handleLogout = () => {
        localStorage.clear();
        setUserRole(null);
        setUsername('');
        alert('Sesión cerrada.');
    };

    const handleLogin = async (username, password) => {
        try {
            const response = await fetch('https://innovatessport.com/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
            });
    
            const data = await response.json();
            
            if (response.ok) {
                localStorage.setItem('token', data.token);
                localStorage.setItem('role', data.user.role);
                localStorage.setItem('username', data.user.username);
                localStorage.setItem('userId', data.user.id);
                localStorage.setItem('slug', data.user.slug);
                
                setUserRole(data.user.role);
                setUsername(data.user.username);
                setModalOpen(false);
                
                // Redirección según rol
                if (data.user.role === 'Administrador') {
                    window.location.href = `/admin/dashboard/${data.user.slug}`;
                }
                // Superadministrador se queda en Home
            } else {
                alert(data.message || 'Credenciales incorrectas');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Ocurrió un error. Intenta nuevamente.');
        }
    };

    return (
        <div className="is-home">
            <Helmet>
                <title>InovateSport | Home</title>
                <meta name="description" content="Tu plataforma para eventos deportivos." />
                <link rel="icon" href="/favicon.ico" />
            </Helmet>

            <Header 
                userRole={userRole}
                username={username}
                handleLogout={handleLogout}
                setModalOpen={setModalOpen}
                setRegisterModalOpen={setRegisterModalOpen}
            />

            <main className="main-content">
                {/* 1. HERO BANNER PRINCIPAL */}
                <section className="hero-banner-clean" style={{
                    backgroundImage: ` url(${bannerImage}`
                }}>
                    <div className="banner-content-clean">
                        <div className="banner-text-left">
                            <h1 className="banner-title-clean">
                                La plataforma integral <br />
                                para <span className="highlight-text">eventos deportivos</span>
                            </h1>
                            <p className="banner-subtitle-clean">
                                Inscripciones online, cronometraje electrónico, resultados en vivo, 
                                asesoramiento y producción para eventos deportivos.
                            </p>
                            <div className="banner-actions-h">
                                <button className="btn-primary-purple">CREAR MI EVENTO <FaChevronRight /></button>
                                <button className="btn-secondary-outline"><FaCalendarAlt /> VER EVENTOS ABIERTOS</button>
                            </div>
                            <div className="banner-features-strip">
                                <div className="feature-item">
                                    <FiZap className="feature-icon stretched-icon" style={{ stroke: "url(#blue-purple-gradient)" }} />
                                    Tecnología propia
                                </div>

                                <div className="feature-item">
                                    <LuHeadset className="feature-icon" style={{ stroke: "url(#blue-purple-gradient)" }} />
                                    Soporte personalizado
                                </div>

                                <div className="feature-item">
                                    <LuShieldCheck
                                        className="feature-icon"
                                        style={{ stroke: "url(#blue-purple-gradient)" }}
                                    />
                                    Resultados confiables
                                    </div>
                                </div>
                        </div>
                    </div>
                </section>

                {/* 2. SECCIÓN: NUESTROS SERVICIOS */}
                <section className="services-grid-section">
                    <span className="section-subtitle-tag">NUESTROS SERVICIOS</span>
                    <h2 className="section-title-clean">Todo lo que <span className="text-purple">tu evento</span> necesita</h2>
                    
                    <div className="services-grid-container">
                        {products.map((item, index) => (
                            <div className="service-card-clean" key={index}>
                                <div className="service-icon-wrapper">{item.icon}</div>
                                <h3>{item.title}</h3>
                                <p>{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* 3. SECCIÓN: NUESTRO PROCESO */}
                <section className="process-flow-section">
                    <span className="section-subtitle-tag">NUESTRO PROCESO</span>
                    <h2 className="section-title-clean">
                        Nos encargamos de la tecnología para que <span className="text-purple">tú</span> te concentres en <span className="text-purple">tu evento</span>.
                    </h2>

                    <div className="process-flow-container">
                        {pasosProceso.map((paso, index) => (
                            <div className="process-step" key={index}>
                                <div className="step-icon-circle">
                                    {paso.icon}
                                    {index < pasosProceso.length - 1 && <span className="step-arrow-line">➔</span>}
                                </div>
                                <h4>{paso.title}</h4>
                                <p>{paso.desc}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* 4. FRANJA AZUL DE MÉTRICAS */}
                <section className="metrics-bar-section">

                    <div className="metric-box">
                    <FaRunning className="metric-icon" />
                    <div className="metric-text">
                        <h3>+500</h3>
                        <p>Eventos gestionados</p>
                    </div>
                    </div>

                    <div className="metric-box">
                    <LuUsers className="metric-icon" />
                    <div className="metric-text">
                        <h3>+50.000</h3>
                        <p>Participantes</p>
                    </div>
                    </div>

                    <div className="metric-box">
                    <LuTimer className="metric-icon" />
                    <div className="metric-text">
                        <h3>+300</h3>
                        <p>Carreras cronometradas</p>
                    </div>
                    </div>

                    <div className="metric-box">
                    <LuStar className="metric-icon" />
                    <div className="metric-text">
                        <h3>98%</h3>
                        <p>Satisfacción de clientes</p>
                    </div>
                    </div>

                </section>

                {/* 5. PRÓXIMOS EVENTOS (TU COMPONENTE ORIGINAL) */}
                <section className="featured-events-wrapper">
                    <div className="events-header-row">
                        <div>
                            <span className="section-subtitle-tag">PRÓXIMOS EVENTOS</span>
                            <h2 className="section-title-left">Eventos destacados</h2>
                        </div>
                        <Link to="/Eventos" className="view-all-link">VER TODOS LOS EVENTOS →</Link>
                    </div>
                    <EventsSection userRole={userRole} />
                </section>

                {/* 6. POR QUÉ ELEGIRNOS */}
                <section className="why-choose-section">
                    <span className="section-subtitle-tag">¿POR QUÉ ELEGIR INNOVATESPORT?</span>
                    <div className="why-grid">
                        <div className="why-item">
                            <FaCode className="why-icon" />
                            <div><h4>Tecnología propia</h4><p>Plataforma desarrollada especialmente para eventos deportivos.</p></div>
                        </div>
                        <div className="why-item">
                            <FaUserClock className="why-icon" />
                            <div><h4>Soporte personalizado</h4><p>Atención antes, durante y después de tu evento.</p></div>
                        </div>
                        <div className="why-item">
                            <FaShieldAlt className="why-icon" />
                            <div><h4>Resultados confiables</h4><p>Cronometraje de alta precisión y resultados en tiempo real.</p></div>
                        </div>
                        <div className="why-item">
                            <FaStar className="why-icon" />
                            <div><h4>Experiencia real</h4><p>Conocimiento práctico de eventos deportivos.</p></div>
                        </div>
                    </div>
                </section>

                {/* 7. TESTIMONIOS */}
                <section className="testimonials-section">
                    <span className="section-subtitle-tag">LO QUE DICEN LOS ORGANIZADORES</span>
                    <h2 className="section-title-clean">Testimonios</h2>
                    <div className="testimonials-container">
                        {testimonios.map((t, i) => (
                            <div className="testimonial-card" key={i}>
                                <img src={t.img} alt={t.name} className="avatar-img" />
                                <p className="testimonial-text">{t.text}</p>
                                <h5 className="testimonial-name">{t.name}</h5>
                                <span className="testimonial-event">{t.event}</span>
                            </div>
                        ))}
                    </div>
                </section>

                {/* 8. LLAMADO A LA ACCIÓN FINAL (CTA) */}
                <section className="cta-bottom-banner">
                    <div className="cta-left-content">
                        <span className="cta-tag">¿ORGANIZAS EVENTOS DEPORTIVOS?</span>
                        <h2>Hagamos crecer tu próximo evento juntos</h2>
                        <p>Solicita una demostración gratuita y descubre todo lo que podemos hacer por ti.</p>
                    </div>
                    <div className="cta-right-actions">
                        <button className="btn-white-purple"><FaCalendarAlt /> AGENDAR REUNIÓN</button>
                        <button className="btn-whatsapp"><FaWhatsapp /> CONTACTAR POR WHATSAPP</button>
                    </div>
                </section>
            </main>

            <footer>
                <Footer />
            </footer>
            <LoginModal 
                isOpen={isModalOpen} 
                onClose={() => setModalOpen(false)} 
                onLogin={handleLogin}  // ← Necesitas crear esta función
            />
            <RegisterAdminModal
                isOpen={registerModalOpen}
                onClose={() => setRegisterModalOpen(false)}
            />
        </div>
    );
};

export default Home;